import { dayjs } from '@/extendedDayjs';
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
    selectActivePilotForRelease,
    selectAppHasSelectCapabilitiesConfigured,
    selectAppTimeFormatSetting,
    selectCLIUsers,
    selectUserById,
    selectUserCanPerformAction,
} from '../selectors/selectors';
import { displayUserNameShort } from '../helpers/displayHelpers';
import { RootState } from '../reducers/reducers';
import {
    ChecklistItem,
    ChecklistItemStatusType,
    DefaultUserGroupId,
    IntegrationCapability,
    OrgUser,
    RelativeTimeTrigger,
    UserGroup,
    UserGroupId,
} from '../types';
import { antGrey, getAvatarColorForName, runwayColors, tagStyle } from './styles/globalStyles';
import Avatar from 'antd/lib/avatar';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Paragraph from 'antd/lib/typography/Paragraph';
import { getLocaleDateTimeFormat } from '../timeHelpers';
import { RunwayUserAction } from '../helpers/aclHelpers';
import Checkbox from 'antd/lib/checkbox';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import Tag from 'antd/lib/tag';
import { OwnerAvatar, OwnerAvatarGroup, ReleasePilotAvatar } from './Avatars';
import PingButton from './PingButton';
import GroupTooltip from './checklistItems/GroupTooltip';
import Divider from 'antd/lib/divider';
import { TargetCompletionDueView } from '@/components/ChecklistItemTargetTime';
import { ApartmentOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { createOrgSettingsRoute } from '../helpers/routeHelpers';
import { OrgSettingsTabType } from '@/typescriptHelpers';

interface ChecklistItemRowComponentProps extends ChecklistItem, ReleaseComponentProps {
    isEditing: boolean;
    showAsComplete: boolean;
    onClickInfo: (id: string, title: string, description?: string) => void;
    itemStatusChanged: (id: string, status?: ChecklistItemStatusType) => void;
    isDisabled: boolean;
    orgGroups: Record<UserGroupId, UserGroup>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    dueDate: Nullable<string>;
    targetCompletion?: RelativeTimeTrigger;
}

export const orgLevelCLIIcon = (orgId: string) => {
    return (
        <Tooltip
            title={
                <>
                    {' '}
                    This is an organization-level checklist item that may affect multiple workflows. Visit{' '}
                    <Link
                        to={createOrgSettingsRoute(orgId, OrgSettingsTabType.Checklists)}
                        style={{ color: runwayColors.white, textDecoration: 'underline' }}
                    >
                        Organization settings
                    </Link>{' '}
                    to make changes.
                </>
            }
        >
            <ApartmentOutlined style={{ marginRight: 8, color: antGrey.grey6 }} />
        </Tooltip>
    );
};

const ChecklistItemRowComponent: React.FC<ChecklistItemRowComponentProps> = (props: ChecklistItemRowComponentProps) => {
    const { orgId, appId, releaseId, isEditing, onClickInfo, showAsComplete, isDisabled } = props;
    const checkboxRef = React.useRef(null);
    const cliOwnerUsers = useSelector((state: RootState) => selectCLIUsers(state, orgId, appId, props.owners));
    const hasCommsIntegrationConnected = useSelector((state: RootState) =>
        selectAppHasSelectCapabilitiesConfigured(state, appId, [IntegrationCapability.Notifications]),
    );

    const hasIndividualOwners = props.owners?.length > 0;
    const hasOneOwner = props.owners?.length === 1;
    const hasMultipleOwners = props.owners?.length > 1;

    const activeReleasePilotUser = useSelector((state: RootState) =>
        selectActivePilotForRelease(state, orgId, releaseId),
    );
    const ownedByPilot =
        hasOneOwner &&
        activeReleasePilotUser?.id === props.owners[0] &&
        props.approverGroups.length === 1 &&
        props.approverGroups[0] === DefaultUserGroupId.Pilot;

    const userCanToggleCLI = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAToggleApprovalCLI, props.releaseId),
    );

    const approvedByUser = useSelector((state: RootState) => {
        if (props.status) {
            return selectUserById(state, orgId, appId, props.status.createdBy);
        }

        return null;
    });

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const dateFormat = getLocaleDateTimeFormat('short', use24HourTimeFormat);

    const avatar = (displayName: string) => (
        <Avatar
            size="small"
            src={approvedByUser.commsAvatars?.image32}
            style={{
                backgroundColor: getAvatarColorForName(displayName),
                opacity: opacity,
            }}
        >
            {displayName.charAt(0).toUpperCase()}
        </Avatar>
    );

    const approvedByUserAvatar = (user: OrgUser) => {
        const displayName = displayUserNameShort(user);
        return isDisabled ?
                <>{avatar(displayName)}</>
            :   <Tooltip title={displayName} placement="topRight">
                    <>{avatar(displayName)}</>
                </Tooltip>;
    };

    const textColor = isDisabled ? antGrey.grey6 : antGrey.grey8;
    const opacity = isDisabled ? '0.3' : '1.0';
    const baseStyle: React.CSSProperties = { paddingLeft: 12 };

    const checkbox =
        userCanToggleCLI ?
            <Checkbox
                style={baseStyle}
                checked={props.showAsComplete}
                onChange={() => {
                    const isChecked = !!props.status;
                    const updatedCheckedStatus = !isChecked;
                    props.itemStatusChanged(
                        props.id,
                        updatedCheckedStatus === true ? ChecklistItemStatusType.Approved : null,
                    );

                    checkboxRef.current.blur();
                }}
                ref={checkboxRef}
                disabled={isDisabled}
            />
        :   <Tooltip title="You don't have permission to perform this action. Please contact your organization's admin.">
                <Checkbox
                    disabled
                    style={baseStyle}
                    checked={props.showAsComplete}
                    onChange={() => {
                        const isChecked = !!props.status;
                        const updatedCheckedStatus = !isChecked;
                        props.itemStatusChanged(
                            props.id,
                            updatedCheckedStatus === true ? ChecklistItemStatusType.Approved : null,
                        );
                    }}
                />
            </Tooltip>;

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <Space>
                <Space>
                    {isEditing ? null : checkbox}
                    <div
                        style={{
                            paddingRight: 8,
                            paddingLeft: 8,
                        }}
                    >
                        <Paragraph
                            style={{
                                marginBottom: 0,
                                flexShrink: 1,
                                textDecoration: showAsComplete ? 'line-through' : 'none',
                                color: textColor,
                            }}
                        >
                            {!!props.orgTemplateId && orgLevelCLIIcon(props.orgId)}
                            {props.title}
                        </Paragraph>
                    </div>
                    {props.description && (
                        <Button
                            onClick={() => onClickInfo(props.id, props.title, props.description)}
                            size="small"
                            shape="round"
                            disabled={isDisabled}
                        >
                            Info
                        </Button>
                    )}
                </Space>
                {props.oneOffForVersion && (
                    <Tag
                        color={isDisabled ? antGrey.grey4 : antGrey.grey6}
                        style={{ ...tagStyle, lineHeight: '10px', padding: '2px 4px' }}
                    >
                        {props.oneOffForVersion}
                    </Tag>
                )}
            </Space>

            <Space direction="horizontal" size="middle">
                {props.targetCompletion?.enabled && !props.showAsComplete && !!props.dueDate && (
                    <div style={{ display: 'flex', columnGap: 16 }}>
                        <TargetCompletionDueView isDisabled={isDisabled} dueDate={props.dueDate} />
                        <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch', margin: 0 }} />
                    </div>
                )}

                {props.showAsComplete && (
                    <Space size="middle" style={{ display: 'flex' }}>
                        <Text
                            style={{
                                color: runwayColors.statusGreen,
                                opacity: opacity,
                            }}
                        >
                            Completed
                        </Text>
                        {props.status?.createdAt && (
                            <div style={{ display: 'flex', columnGap: 16 }}>
                                <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch', margin: 0 }} />
                                <Text type="secondary" style={{ opacity: opacity }}>
                                    {dayjs(props.status?.createdAt).format(dateFormat)}
                                </Text>
                            </div>
                        )}
                    </Space>
                )}

                {props.showAsComplete && approvedByUser && approvedByUserAvatar(approvedByUser)}
                {!props.showAsComplete &&
                    props.approverGroups &&
                    props.approverGroups.length > 0 &&
                    !hasIndividualOwners && (
                        <>
                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 14,
                                    opacity: opacity,
                                }}
                            >
                                owned by
                            </Text>
                            {props.approverGroups.map((groupId, index) => {
                                const omitComma = index === props.approverGroups.length - 1;
                                const group = props.orgGroups[groupId];
                                return (
                                    <div>
                                        <GroupTooltip
                                            key={index}
                                            orgId={props.orgId}
                                            appId={props.appId}
                                            releaseId={props.releaseId}
                                            group={group}
                                            showComma={!omitComma}
                                            isDisabled={isDisabled}
                                        />
                                    </div>
                                );
                            })}
                        </>
                    )}

                {!props.showAsComplete && hasIndividualOwners && cliOwnerUsers[0] && (
                    <>
                        <Text
                            type="secondary"
                            style={{
                                fontSize: 14,
                                opacity: opacity,
                            }}
                        >
                            owned by
                        </Text>
                        {hasOneOwner && (
                            <Space size="small" style={{ display: 'flex' }}>
                                <OwnerAvatar user={cliOwnerUsers[0]} isDisabled={isDisabled} />
                                <Text style={{ color: textColor }}>{displayUserNameShort(cliOwnerUsers[0])}</Text>
                                {ownedByPilot && <ReleasePilotAvatar isDisabled={isDisabled} />}
                            </Space>
                        )}
                        {hasMultipleOwners && (
                            <OwnerAvatarGroup maxAvatars={3} owners={cliOwnerUsers} isDisabled={isDisabled} />
                        )}
                    </>
                )}

                {!isEditing && (
                    <div>
                        <PingButton
                            orgId={orgId}
                            appId={appId}
                            releaseId={releaseId}
                            checklistItemId={props.id}
                            text={`${hasMultipleOwners ? 'Ping all' : 'Ping'}`}
                            disabled={props.showAsComplete || !hasCommsIntegrationConnected}
                            disablePopover={isDisabled}
                            missingIntegration={!hasCommsIntegrationConnected}
                            stepRunComponentProps={props.stepRunComponentProps}
                        />
                    </div>
                )}
            </Space>
        </div>
    );
};

export default ChecklistItemRowComponent;
